/** <dialog>-based sheets: a floating bottom sheet, or a centred question; and the glass menus. */
import { h, icon, ICONS } from './dom.ts';
import { trackGlass } from './glass.ts';
import { padField, padFor, type PadKind } from './fieldpad.ts';

/** What is open over the screen — sheets, menus, popovers — each by its way to put it away. */
const open = new Set<() => void>();
let menus = 0;
function opened(close: () => void, menu = true): void {
  open.add(close);
  if (menu) menus++;
}
/** A screen left puts away everything it opened: nothing stays over the next one, nothing acts on what is gone. */
export function closeAll(): void {
  for (const c of [...open]) c();
}
/** A menu or popover is open (the keys are its, not the plan's: Delete never acts behind it). */
export function menuOpen(): boolean {
  return menus > 0;
}
/**
 * A menu or popover put away. A touch on its backdrop puts it away and does nothing else: the
 * backdrop stays under the finger until that tap is over, so the tap never lands on what lies under
 * it (an Undo, a Back) — gone at its click, or a moment after.
 */
function shut(close: () => void, backdrop: HTMLElement, byBackdrop: boolean): void {
  open.delete(close);
  menus = Math.max(0, menus - 1);
  if (!byBackdrop) return backdrop.remove();
  const gone = () => backdrop.remove();
  backdrop.addEventListener('click', gone, { once: true });
  setTimeout(gone, 600);
}

export interface SheetHandle {
  dialog: HTMLDialogElement;
  body: HTMLElement;
  panel: HTMLElement;
  close(): void;
}

/** A centred sheet keeps to the middle of what can be seen: above the keyboard when it is up. */
function trackViewport(dialog: HTMLElement): () => void {
  const vv = window.visualViewport;
  if (!vv) return () => {};
  const apply = () => {
    dialog.style.setProperty('--vv-top', `${vv.offsetTop}px`);
    dialog.style.setProperty('--vv-height', `${vv.height}px`);
  };
  apply();
  vv.addEventListener('resize', apply);
  vv.addEventListener('scroll', apply);
  return () => {
    vv.removeEventListener('resize', apply);
    vv.removeEventListener('scroll', apply);
  };
}

export function openSheet(title: string, opts: { center?: boolean; onClose?: () => void; closeLabel?: string } = {}): SheetHandle {
  const body = h('div', { class: 'sheet-body' });
  const closeBtn = h('button', { class: 'btn icon', 'aria-label': opts.closeLabel ?? 'Close', type: 'button' }, icon(ICONS.close, 16));
  // the sheet itself takes the focus a dialog must give to something: no ring lights up on the close button
  const panel = h('div', { class: 'panel', tabindex: '-1', autofocus: true }, h('div', { class: 'sheet-header' }, h('h2', null, title), closeBtn), body);
  const dialog = h('dialog', { class: 'sheet' + (opts.center ? ' center' : '') }, panel);
  const untrack = opts.center ? trackViewport(dialog) : () => {};
  // it leaves the way it came, and only then is it gone
  let closing = false;
  const close = () => {
    if (!dialog.open || closing) return;
    closing = true;
    dialog.classList.add('closing');
    const done = () => dialog.open && dialog.close();
    panel.addEventListener('animationend', done, { once: true });
    setTimeout(done, 320);
  };
  closeBtn.addEventListener('click', close);
  // a tap on the backdrop puts it away — a tap begun there, and only that: a field tapped as the sheet
  // moves aside for the number pad (or a press slid off the sheet) ends its click on the backdrop, and
  // must not take the sheet away (2026-10-24, Adam: "instead it just goes away (the whole popup)")
  let downOnBackdrop = false;
  dialog.addEventListener('pointerdown', (e) => {
    downOnBackdrop = e.target === dialog;
  });
  dialog.addEventListener('click', (e) => {
    const was = downOnBackdrop;
    downOnBackdrop = false;
    if (e.target === dialog && was) close();
  });
  dialog.addEventListener('close', () => {
    open.delete(close);
    untrack();
    unglass();
    dialog.remove();
    opts.onClose?.();
  });
  dialog.addEventListener('cancel', (e) => {
    e.preventDefault();
    close();
  });
  document.body.append(dialog);
  dialog.showModal();
  // (glass, as every floating thing: drawn from the plan under it, dimmed by the sheet's backdrop)
  const unglass = trackGlass(panel, { gpu: false });
  opened(close, false);
  return { dialog, body, panel, close };
}

export function confirmSheet(title: string, message: string, confirmLabel = 'Delete', danger = true): Promise<boolean> {
  return new Promise((resolve) => {
    let result = false;
    const s = openSheet(title, { center: true, onClose: () => resolve(result) });
    s.body.append(h('p', { class: 'help body' }, message));
    s.panel.append(
      h(
        'div',
        { class: 'sheet-actions' },
        h('button', { class: 'btn subtle', type: 'button', onClick: () => s.close() }, 'Cancel'),
        h(
          'button',
          {
            class: 'btn primary' + (danger ? ' destructive' : ''),
            type: 'button',
            onClick: () => {
              result = true;
              s.close();
            },
          },
          confirmLabel,
        ),
      ),
    );
  });
}

export function promptSheet(title: string, opts: { value?: string; placeholder?: string; label?: string; mono?: boolean; multiline?: boolean; okLabel?: string; help?: string; inputmode?: string; pad?: PadKind; padKey?: '+' | '±' | '-'; suggestions?: { label: string; items: string[] }[] }): Promise<string | null> {
  return new Promise((resolve) => {
    let result: string | null = null;
    const s = openSheet(title, { center: true, onClose: () => resolve(result) });
    const input = opts.multiline
      ? h('textarea', { class: 'input' + (opts.mono ? ' mono' : ''), rows: 3, placeholder: opts.placeholder ?? '' })
      : h('input', { class: 'input' + (opts.mono ? ' mono' : ''), type: 'text', placeholder: opts.placeholder ?? '', autocapitalize: opts.inputmode ? 'off' : 'sentences', autocorrect: 'off', spellcheck: false, enterkeyhint: 'done', ...(opts.inputmode ? { inputmode: opts.inputmode } : {}) });
    input.value = opts.value ?? '';
    // how it is typed: with the number pad alone (a number), the pad and the keyboard a key away (numbers mostly), or the keyboard with the pad beside it (words)
    padField(input, opts.pad ?? (opts.inputmode === 'numeric' || opts.inputmode === 'decimal' ? 'num' : 'text'), { ...(opts.padKey ? { key: opts.padKey } : {}), ...(opts.inputmode === 'numeric' ? { point: false } : {}) });
    const submit = () => {
      result = input.value;
      s.close();
    };
    input.addEventListener('keydown', (e: Event) => {
      if ((e as KeyboardEvent).key === 'Enter' && !opts.multiline) {
        e.preventDefault();
        submit();
      }
    });
    s.body.append(h('div', { class: 'field' }, opts.label ? h('label', null, opts.label) : null, input, opts.help ? h('p', { class: 'help' }, opts.help) : null));
    // words ready to use: a tap takes one (and is done)
    for (const group of opts.suggestions ?? []) {
      if (!group.items.length) continue;
      s.body.append(
        h('div', { class: 'field' }, h('label', null, group.label), h('div', { class: 'chips wrap' }, ...group.items.map((t) => h('button', { class: 'chip', type: 'button', onClick: () => ((input.value = t), submit()) }, t)))),
      );
    }
    s.panel.append(h('div', { class: 'sheet-actions' }, h('button', { class: 'btn subtle', type: 'button', onClick: () => s.close() }, 'Cancel'), h('button', { class: 'btn primary', type: 'button', onClick: submit }, opts.okLabel ?? 'Done')));
    // with words ready to tap, the keyboard waits for a tap on the field: up at once it would cover them
    if (!opts.suggestions?.some((g) => g.items.length))
      requestAnimationFrame(() => {
        input.focus({ preventScroll: true });
        if (!opts.multiline) (input as HTMLInputElement).select();
        // (the pad comes with the sheet, the caret in its field or not)
        padFor(input);
      });
  });
}

export interface MenuItem {
  label: string;
  icon?: string;
  danger?: boolean;
  /** A switch in the menu: on shows a check at its end. */
  checked?: boolean;
  /** A few quiet words at its end (what it is now: "#7", "Elev."). */
  detail?: string;
  onSelect: () => void;
}
/** Between two sections of a menu. */
export const MENU_SEP = 'sep';

/**
 * A glass popover of anything (a palette, a few choices with a field) that grows out of its button,
 * as a menu does, and goes back into it; `build` is handed the way to close it.
 */
export function openPopover(anchor: HTMLElement, build: (close: () => void) => (Node | null)[], opts: { above?: boolean; label?: string } = {}): () => void {
  const pop = h('div', { class: 'glass menu popover', role: 'dialog', 'aria-label': opts.label ?? '' });
  // a choice made in a popover leaves the keyboard where it was (a callout being typed goes on); its own fields still take a tap
  pop.addEventListener('mousedown', (e) => {
    if (!(e.target instanceof HTMLInputElement || e.target instanceof HTMLLabelElement)) e.preventDefault();
  });
  const backdrop = h('div', { class: 'menu-backdrop' });
  let closed = false;
  // (glass kept up with the plan under it while it is open: ui/glass.ts)
  let unglass = () => {};
  const close = (byBackdrop = false) => {
    if (closed) return;
    closed = true;
    unglass();
    shut(close, backdrop, byBackdrop);
    document.removeEventListener('keydown', onKey, true);
    pop.classList.remove('show');
    pop.style.pointerEvents = 'none';
    pop.inert = true;
    pop.setAttribute('aria-hidden', 'true');
    pop.addEventListener('transitionend', () => pop.remove(), { once: true });
    setTimeout(() => pop.remove(), 260);
  };
  const onKey = (e: KeyboardEvent) => {
    if (e.key !== 'Escape') return;
    e.stopPropagation();
    close();
  };
  for (const n of build(() => close())) if (n) pop.append(n);
  backdrop.addEventListener('pointerdown', () => close(true));
  document.addEventListener('keydown', onKey, true);
  document.body.append(backdrop, pop);
  opened(close);
  unglass = trackGlass(pop, { gpu: false });
  place(pop, anchor, opts.above ?? false);
  return () => close();
}
/** Put a menu or popover by its button: below it (or above, when asked or when there is no room below), on screen, opening from the button. */
function place(menu: HTMLElement, anchor: HTMLElement, preferAbove: boolean): void {
  const r = anchor.getBoundingClientRect();
  const mh = menu.offsetHeight,
    mw = menu.offsetWidth;
  const fitsAbove = r.top - 8 - mh >= 8,
    fitsBelow = r.bottom + 8 + mh <= window.innerHeight - 8;
  const above = preferAbove ? fitsAbove || !fitsBelow : !fitsBelow && fitsAbove;
  const top = Math.max(8, Math.min(window.innerHeight - mh - 8, above ? r.top - 8 - mh : r.bottom + 8));
  const left = Math.max(8, Math.min(window.innerWidth - mw - 8, r.left + r.width / 2 - mw / 2));
  menu.style.top = `${top}px`;
  menu.style.left = `${left}px`;
  const ox = Math.max(0, Math.min(mw, r.left + r.width / 2 - left));
  menu.style.transformOrigin = `${ox}px ${above ? mh : 0}px`;
  void menu.offsetWidth;
  menu.classList.add('show');
}

/** A glass menu that grows out of its button, and goes back into it; `MENU_SEP` parts its sections. */
export function openMenu(anchor: HTMLElement, items: (MenuItem | typeof MENU_SEP)[], opts: { above?: boolean } = {}): void {
  const menu = h('div', { class: 'glass menu', role: 'menu' });
  // a choice made in a menu leaves the keyboard where it was (a field being typed into goes on)
  menu.addEventListener('mousedown', (e) => e.preventDefault());
  for (const it of items) {
    if (it === MENU_SEP) {
      // (never first, never last, never two together: a section left empty leaves no line)
      if (menu.lastElementChild && !menu.lastElementChild.classList.contains('sep')) menu.append(h('div', { class: 'sep', role: 'separator' }));
      continue;
    }
    menu.append(
      h(
        'button',
        {
          class: 'btn' + (it.danger ? ' danger' : ''),
          type: 'button',
          // a switch says whether it is on (as the check at its end shows)
          role: it.checked === undefined ? 'menuitem' : 'menuitemcheckbox',
          'aria-checked': it.checked === undefined ? undefined : String(it.checked),
          onClick: () => {
            close();
            it.onSelect();
          },
        },
        it.icon ? icon(it.icon, 21) : null,
        h('span', { class: 'label' }, it.label),
        it.detail ? h('span', { class: 'detail' }, it.detail) : null,
        it.checked ? icon(ICONS.check, 18, 'tick') : null,
      ),
    );
  }
  if (menu.lastElementChild?.classList.contains('sep')) menu.lastElementChild.remove();
  const backdrop = h('div', { class: 'menu-backdrop' });
  let closed = false;
  // (glass kept up with the plan under it while it is open: ui/glass.ts)
  let unglass = () => {};
  const close = (byBackdrop = false) => {
    if (closed) return;
    closed = true;
    unglass();
    shut(close, backdrop, byBackdrop);
    document.removeEventListener('keydown', onKey, true);
    menu.classList.remove('show');
    menu.style.pointerEvents = 'none';
    // (gone at once for touch and for assistive tech: only its picture fades)
    menu.inert = true;
    menu.setAttribute('aria-hidden', 'true');
    menu.addEventListener('transitionend', () => menu.remove(), { once: true });
    setTimeout(() => menu.remove(), 260);
  };
  const onKey = (e: KeyboardEvent) => {
    if (e.key !== 'Escape') return;
    e.stopPropagation();
    close();
  };
  backdrop.addEventListener('pointerdown', () => close(true));
  document.addEventListener('keydown', onKey, true);
  document.body.append(backdrop, menu);
  opened(close);
  unglass = trackGlass(menu, { gpu: false });
  const r = anchor.getBoundingClientRect();
  // below its button — or above it, when asked to (the button's item lies below) or where there is no room below
  const mh = menu.offsetHeight,
    mw = menu.offsetWidth;
  const fitsAbove = r.top - 8 - mh >= 8,
    fitsBelow = r.bottom + 8 + mh <= window.innerHeight - 8;
  const above = opts.above ? fitsAbove || !fitsBelow : !fitsBelow && fitsAbove;
  const top = Math.max(8, Math.min(window.innerHeight - mh - 8, above ? r.top - 8 - mh : r.bottom + 8));
  // lined up with its button's leading edge and opening away from it, over what the button is
  // beside rather than back over what it acts on; from the trailing edge where there is no room
  const lead = r.left - 6;
  const left = Math.max(8, Math.min(window.innerWidth - mw - 8, lead + mw <= window.innerWidth - 8 ? lead : r.right + 6 - mw));
  menu.style.top = `${top}px`;
  menu.style.left = `${left}px`;
  // it opens from the button itself
  const ox = Math.max(0, Math.min(mw, r.left + r.width / 2 - left));
  menu.style.transformOrigin = `${ox}px ${above ? mh : 0}px`;
  void menu.offsetWidth;
  menu.classList.add('show');
}
